import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Save } from "lucide-react";
import { siteRetentionSchema, type SiteRetention } from "@wedding/shared";
import { adminSiteApi } from "../lib/api";

/**
 * "Satış & Site → Saklama": landing'deki "albüm en az N ay açık" vaadi
 * (SSS + plan notu). Kod onaylı medyayı otomatik silmez; bu sayı yalnız
 * kamuya verilen ALT sınırdır — artırmak serbest, azaltmak mevcut müşteriye
 * verilmiş sözü değiştirmez (uyarı metninde).
 */
export function RetentionTab({ initial, onSaved, showToast }: {
  initial: SiteRetention;
  onSaved: (r: SiteRetention) => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t } = useTranslation();
  const [base, setBase] = useState(initial);
  const [show, setShow] = useState(initial.show);
  const [months, setMonths] = useState(String(initial.months));
  const [saving, setSaving] = useState(false);

  const parsed = siteRetentionSchema.safeParse({ show, months: Number(months) });
  const dirty = show !== base.show || months !== String(base.months);

  const save = async () => {
    if (!parsed.success) {
      showToast(t("siteAdmin.invalid"), false);
      return;
    }
    setSaving(true);
    try {
      const res = await adminSiteApi.put("retention", parsed.data);
      const saved = res.data ?? parsed.data;
      setBase(saved);
      onSaved(saved);
      showToast(t("siteAdmin.saved"));
    } catch (err) {
      showToast(t("siteAdmin.saveFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border bg-muted/30">
        <h2 className="text-sm font-semibold text-foreground">{t("siteAdmin.retention.title")}</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.retention.desc")}</p>
      </div>
      <div className="space-y-4 px-5 py-4">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} className="h-4 w-4 accent-primary cursor-pointer" />
          {t("siteAdmin.retention.show")}
        </label>
        <div className="max-w-xs space-y-1.5">
          <label htmlFor="retention-months" className="text-xs font-medium text-foreground">{t("siteAdmin.retention.months")}</label>
          <input
            id="retention-months"
            inputMode="numeric"
            value={months}
            onChange={(e) => setMonths(e.target.value)}
            aria-invalid={!parsed.success || undefined}
            className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/20 aria-[invalid=true]:border-red-500"
          />
        </div>
        <p className="rounded-xl border border-dashed border-border px-4 py-3 text-sm text-muted-foreground" data-testid="retention-preview">
          {show && parsed.success
            ? t("landing.plans.retention", { months: parsed.data.months })
            : t("siteAdmin.retention.hidden")}
        </p>
        <p className="text-xs text-amber-700 dark:text-amber-400">{t("siteAdmin.retention.warn")}</p>
      </div>
      <div className="flex justify-end px-5 pb-4">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {saving ? t("siteAdmin.saving") : t("siteAdmin.save")}
        </button>
      </div>
    </div>
  );
}
